import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";

/** Datos mínimos para identificar el documento dueño de las líneas. */
type DocumentReference = {
    id?: string | null,
    numero?: number | string | null
};

export type ProductLinesIssue = {
    documentKind: string,
    id: string,
    numero: string,
    droppedLines: number
};

/** Documentos con líneas ilegibles, indexados para no reportar el mismo dos veces. */
export type ProductLinesIssues = Map<string, ProductLinesIssue>;

export const DOCUMENT_KIND_INVOICE = "Factura";
export const DOCUMENT_KIND_CREDITNOTE = "Nota de crédito";
export const DOCUMENT_KIND_DEBITNOTE = "Nota de débito";

export const createProductLinesIssues = (): ProductLinesIssues => new Map();

const registerIssue = (issues: ProductLinesIssues, issue: ProductLinesIssue): void => {
    const key = `${issue.documentKind}#${issue.id}`;
    const current = issues.get(key);
    if (current && current.droppedLines >= issue.droppedLines) {
        return;
    }
    issues.set(key, issue);
    console.warn(
        `[Reporte] ${issue.documentKind} #${issue.numero} (${issue.id}): la API devolvió ${issue.droppedLines} línea(s) como null y se excluyeron de los totales. Revisar la fila en la base de datos: le falta algún campo obligatorio del modelo.`
    );
};

/**
 * Descarta las líneas de producto que la API devolvió como `null`.
 *
 * AppSync anula el elemento completo de la lista cuando la fila no trae alguno de los
 * campos obligatorios del modelo (`description`, `cant`, `codigo_tarifa`, `unidad_medida`, …),
 * y el cliente de Amplify se traga esos `errors` al procesar la respuesta parcial
 * (ver `indexQuery` de `@aws-amplify/data-schema`), así que los `null` llegan silenciosos
 * hasta los cálculos del reporte. Sin este filtro una sola fila corrupta rompe el reporte
 * completo con un `Cannot read properties of null`.
 */
export const sanitizeProductLines = (
    products: (EDocumentProductDataType | null)[] | null | undefined,
    document: DocumentReference | null | undefined,
    documentKind: string,
    issues?: ProductLinesIssues
): EDocumentProductDataType[] => {
    if (!products) {
        return [];
    }
    const validProducts = products.filter((product): product is EDocumentProductDataType => product != null);
    const droppedLines = products.length - validProducts.length;
    if (droppedLines > 0 && issues) {
        registerIssue(issues, {
            documentKind,
            id: `${document?.id || "sin id"}`,
            numero: `${document?.numero ?? "sin número"}`,
            droppedLines
        });
    }
    return validProducts;
};

/**
 * Resumen para la consola del navegador con los documentos que quedaron incompletos.
 * A propósito no se imprime en el reporte (PDF ni Excel): es información de diagnóstico
 * para desarrollo, no parte del documento que ve el cliente.
 */
export const logProductLinesIssuesSummary = (issues: ProductLinesIssues, reportName: string): void => {
    if (issues.size === 0) {
        return;
    }
    const rows = Array.from(issues.values());
    console.warn(`[${reportName}] Documentos con líneas que no se pudieron leer (totales incompletos): ${rows.length}`);
    console.table(rows);
};
